import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { PlatformAuthProvider } from './auth/PlatformAuthContext';
import RoleGuard from './components/RoleGuard';
import PlatformGuard from './components/PlatformGuard';
import AppLayout from './layouts/AppLayout';
import LoginPage from './pages/Login';
import PlatformLogin from './pages/platform/PlatformLogin';
import PlatformDashboard from './pages/platform/PlatformDashboard';
import PlatformOnboard from './pages/platform/PlatformOnboard';
import PlatformAnalytics from './pages/platform/PlatformAnalytics';
import PlatformHospitalDetail from './pages/platform/PlatformHospitalDetail';
import PlatformAdmins from './pages/platform/PlatformAdmins';
import PlatformFleetAudit from './pages/platform/PlatformFleetAudit';
import OpQueue from './pages/reception/OpQueue';
import ReceptionPatients from './pages/reception/ReceptionPatients';
import DoctorQueue from './pages/doctor/DoctorQueue';
import IpdList from './pages/ipd/IpdList';
import IpdChart from './pages/ipd/IpdChart';
import IpdAdmit, { BedMaster } from './pages/ipd/IpdAdmit';
import BillingReceipts from './pages/billing/BillingReceipts';
import ManagerDashboard from './pages/manager/ManagerDashboard';
import PharmacyDesk from './pages/pharmacy/PharmacyDesk';
import LabOrders from './pages/lab/LabOrders';
import NursingStation from './pages/nurse/NursingStation';
import HospitalAdmin from './pages/manager/HospitalAdmin';
import HmsModulePage, {
  BillingOpPage,
  AnalysisPage,
  SettingsPage,
} from './pages/hms/HmsModulePage';
import { EmrPage } from './pages/hms/EmrPage';
import RxPadSettingsPage from './pages/hms/RxPadSettingsPage';
import AiSettingsPage from './pages/hms/AiSettingsPage';
import ClinicGrowthPage from './pages/hms/ClinicGrowthPage';
import SmsCenter from './pages/hms/SmsCenter';
import HrDashboard from './pages/hr/HrDashboard';
import StaffLeavePage from './pages/hr/StaffLeavePage';
import BloodBankPage from './pages/hms/BloodBankPage';
import DietManagementPage from './pages/hms/DietManagementPage';
import PatientFeedbackPage from './pages/hms/PatientFeedbackPage';
import MaintenancePage from './pages/hms/MaintenancePage';
import OtManagementPage from './pages/hms/OtManagementPage';
import AppointmentsFullPage from './pages/hms/AppointmentsPage';
import DischargeSummaryPage from './pages/hms/DischargeSummaryPage';
import InventoryPage from './pages/hms/InventoryPage';
import IcuPage from './pages/hms/IcuPage';
import RadiologyPage from './pages/hms/RadiologyPage';
import InfectionControlPage from './pages/hms/InfectionControlPage';
import QualityPage from './pages/hms/QualityPage';
import TelemedicinePage from './pages/hms/TelemedicinePage';
import AbhaPage from './pages/hms/AbhaPage';
import TpaClaimsPage from './pages/hms/TpaClaimsPage';
import BiomedicalWastePage from './pages/hms/BiomedicalWastePage';
import PayrollPage from './pages/hms/PayrollPage';
import AmbulancePage from './pages/hms/AmbulancePage';
import PhysiotherapyPage from './pages/hms/PhysiotherapyPage';
import DialysisPage from './pages/hms/DialysisPage';
import DocumentManagementPage from './pages/hms/DocumentManagementPage';
import MortuaryPage from './pages/hms/MortuaryPage';
import VisitorManagementPage from './pages/hms/VisitorManagementPage';
import DutyRosterPage from './pages/hms/DutyRosterPage';
import NicuPage from './pages/hms/NicuPage';
import HousekeepingPage from './pages/hms/HousekeepingPage';
import VaccinationPage from './pages/hms/VaccinationPage';
import QueueTvDisplay from './pages/reception/QueueTvDisplay';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import { landingPathFor } from './permissions';

function HomeRedirect() {
  const { isLoggedIn, user } = useAuth();
  if (!isLoggedIn) return <Navigate to="/login" replace />;
  return <Navigate to={landingPathFor(user?.role)} replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <PlatformAuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/display/waiting-room" element={<QueueTvDisplay />} />
            <Route path="/platform/login" element={<PlatformLogin />} />
            <Route path="/platform" element={<Navigate to="/platform/dashboard" replace />} />
            <Route
              path="/platform/dashboard"
              element={
                <PlatformGuard>
                  <PlatformDashboard />
                </PlatformGuard>
              }
            />
            <Route
              path="/platform/onboard"
              element={
                <PlatformGuard>
                  <PlatformOnboard />
                </PlatformGuard>
              }
            />
            <Route
              path="/platform/hospitals/:hospitalId"
              element={
                <PlatformGuard>
                  <PlatformHospitalDetail />
                </PlatformGuard>
              }
            />
            <Route
              path="/platform/analytics"
              element={
                <PlatformGuard>
                  <PlatformAnalytics />
                </PlatformGuard>
              }
            />
            <Route
              path="/platform/admins"
              element={
                <PlatformGuard>
                  <PlatformAdmins />
                </PlatformGuard>
              }
            />
            <Route
              path="/platform/audit"
              element={
                <PlatformGuard>
                  <PlatformFleetAudit />
                </PlatformGuard>
              }
            />
            <Route
              path="/"
              element={
                <RoleGuard>
                  <AppLayout />
                </RoleGuard>
              }
            >
              <Route index element={<HomeRedirect />} />
              <Route path="manager/dashboard" element={<ManagerDashboard />} />
              <Route path="manager/admin" element={<HospitalAdmin />} />
              <Route path="reception/queue" element={<OpQueue />} />
              <Route path="reception/patients" element={<ReceptionPatients />} />
              <Route path="doctor/queue" element={<DoctorQueue />} />
              <Route path="billing/receipts" element={<BillingReceipts />} />
              <Route path="billing/op" element={<BillingOpPage />} />
              <Route path="ipd/list" element={<IpdList />} />
              <Route path="ipd/patient/:admissionId" element={<IpdChart />} />
              <Route path="ipd/admit" element={<IpdAdmit />} />
              <Route path="ipd/beds" element={<BedMaster />} />
              <Route path="nurse/station" element={<NursingStation />} />
              <Route path="pharmacy/desk" element={<PharmacyDesk />} />
              <Route path="lab/orders" element={<LabOrders />} />
              <Route path="hr/dashboard" element={<HrDashboard />} />
              <Route path="hr/leave" element={<StaffLeavePage />} />
              <Route path="hr/staff" element={<Navigate to="/hr/dashboard" replace />} />
              <Route path="staff/directory" element={<Navigate to="/hr/dashboard" replace />} />
              <Route path="hms/appointments" element={<AppointmentsFullPage />} />
              <Route path="hms/vaccination" element={<VaccinationPage />} />
              <Route path="hms/patients" element={<ReceptionPatients />} />
              <Route path="hms/emr" element={<EmrPage />} />
              <Route path="hms/rx-settings" element={<RxPadSettingsPage />} />
              <Route path="hms/econsult" element={<Navigate to="/hms/telemedicine" replace />} />
              <Route path="hms/radiology" element={<RadiologyPage />} />
              <Route path="hms/icu" element={<IcuPage />} />
              <Route path="hms/infection" element={<InfectionControlPage />} />
              <Route path="hms/quality" element={<QualityPage />} />
              <Route path="hms/telemedicine" element={<TelemedicinePage />} />
              <Route path="hms/abha" element={<AbhaPage />} />
              <Route path="hms/ai" element={<AiSettingsPage />} />
              <Route path="hms/tpa" element={<TpaClaimsPage />} />
              <Route path="hms/bmw" element={<BiomedicalWastePage />} />
              <Route path="hms/payroll" element={<PayrollPage />} />
              <Route path="hms/ambulance" element={<AmbulancePage />} />
              <Route path="hms/physio" element={<PhysiotherapyPage />} />
              <Route path="hms/dialysis" element={<DialysisPage />} />
              <Route path="hms/documents" element={<DocumentManagementPage />} />
              <Route path="hms/mortuary" element={<MortuaryPage />} />
              <Route path="hms/visitors" element={<VisitorManagementPage />} />
              <Route path="hms/roster" element={<DutyRosterPage />} />
              <Route path="hms/nicu" element={<NicuPage />} />
              <Route path="hms/housekeeping" element={<HousekeepingPage />} />
              <Route path="hms/store" element={<InventoryPage />} />
              <Route path="hms/discharge" element={<DischargeSummaryPage />} />
              <Route path="hms/ot" element={<OtManagementPage />} />
              <Route path="hms/sms" element={<SmsCenter />} />
              <Route path="hms/clinic-growth" element={<ClinicGrowthPage />} />
              <Route path="hms/analysis" element={<AnalysisPage />} />
              <Route path="hms/settings" element={<SettingsPage />} />
              <Route path="hms/bloodbank" element={<BloodBankPage />} />
              <Route path="hms/diet" element={<DietManagementPage />} />
              <Route path="hms/feedback" element={<PatientFeedbackPage />} />
              <Route path="hms/maintenance" element={<MaintenancePage />} />
              <Route path="hms/:moduleId" element={<HmsModulePage />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </PlatformAuthProvider>
    </AuthProvider>
  );
}
